/**
 * Modal de avance de un proceso largo del servidor: calcular la nómina, timbrar,
 * cancelar, o cualquiera de las operaciones masivas de catálogo (edición masiva
 * de insumos, actualización de precios, importaciones).
 *
 * Es BLOQUEANTE a propósito (sin botón de cerrar, sin cerrar con Esc ni con
 * clic afuera): mientras el servidor está recalculando o escribiendo, cualquier
 * cosa que se toque en la pantalla se pierde o pelea con el proceso. Antes esto
 * se resolvía con un `<Spin>` que tapaba todo sin decir nada, y una barra inline
 * que se iba detrás del scroll.
 *
 * Es puramente presentacional: no consulta nada, no tiene estado propio y no
 * decide cuándo cerrarse. Quien lo usa lo abre y lo cierra según los eventos de
 * WebSocket (y el respaldo por polling a Redis).
 *
 * Vivía dentro de PreNomina; se movió aquí cuando las operaciones masivas de
 * insumos necesitaron exactamente lo mismo. Lo único que era de nómina era la
 * palabra "empleados", que ahora entra por `unidad`.
 */

import React from 'react'
import { Button, Modal, Progress, Space, Spin } from 'antd'

// Este modal tiene que quedar SIEMPRE encima de los otros de la pantalla: el de
// validación se queda abierto detrás mientras corre la consulta al SAT.
//
// No basta con el orden del JSX. antd les da z-index 1000 a todos, así que quien
// queda arriba es el que tiene su portal más abajo en el <body>, y ese portal se
// crea la PRIMERA vez que cada modal se abre. O sea que el orden lo decide qué
// modal se abrió antes en la sesión: si el usuario ya había calculado, el portal
// de éste es el más viejo y se va debajo del de validación.
//
// 1005 lo pone encima de los demás modales (1000) pero debajo de message y
// notification (1010), para no tapar los avisos de error.
const Z_INDEX_ENCIMA_DE_MODALES = 1005

const ModalProgreso = ({
  visible,
  titulo,
  descripcion,
  procesados = 0,
  total = null,
  exitosos = null,
  errores = null,
  detalle = null,
  colorBarra,
  unidad = 'empleados',
  // Opcionales, para procesos largos que el servidor sabe detener o que siguen solos
  // aunque se cierre la pantalla (p. ej. la importación de Libros Digitales). Sin ellos
  // el modal es el de siempre, sin pie.
  onDetener = null,
  deteniendo = false,
  textoDetener = 'Detener',
  onOcultar = null,
  textoOcultar = 'Salir (sigue en el servidor)',
}) => {
  const conTotal = typeof total === 'number' && total > 0
  const porcentaje = conTotal
    ? Math.min(100, Math.round((procesados / total) * 100))
    : 0

  const pie = onDetener || onOcultar
    ? (
      <Space>
        {onOcultar && <Button onClick={onOcultar}>{textoOcultar}</Button>}
        {onDetener && (
          <Button danger onClick={onDetener} loading={deteniendo} disabled={deteniendo}>
            {deteniendo ? 'Deteniendo…' : textoDetener}
          </Button>
        )}
      </Space>
    )
    : null

  return (
    <Modal
      visible={visible}
      title={titulo}
      footer={pie}
      closable={false}
      maskClosable={false}
      keyboard={false}
      width={460}
      centered
      zIndex={Z_INDEX_ENCIMA_DE_MODALES}
    >
      {/* Mientras no se sepa el total no se dibuja barra: una al 100 % se lee
          como "ya terminó" y una al 0 % como "está atorado". El servidor manda
          el total en cuanto lo sabe, así que este estado dura poco. */}
      {conTotal ? (
        <Progress percent={porcentaje} status="active" strokeColor={colorBarra} />
      ) : (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <Spin />
        </div>
      )}

      <div style={{ marginTop: 12, fontSize: 15 }}>
        {conTotal
          ? <span><b>{procesados}</b> de <b>{total}</b> {unidad}</span>
          : <span>Preparando…</span>}

        {exitosos !== null && exitosos > 0 && (
          <span style={{ color: '#52c41a', marginLeft: 10 }}>✓ {exitosos}</span>
        )}
        {errores !== null && errores > 0 && (
          <span style={{ color: '#ff4d4f', marginLeft: 10 }}>✗ {errores}</span>
        )}
      </div>

      {/* Qué se está procesando ahora mismo. Sirve para dos cosas: se ve que
          avanza aunque el porcentaje no cambie, y si el proceso se atora queda
          a la vista en dónde fue. */}
      {detalle && (
        <div style={{ marginTop: 6, color: '#8c8c8c', fontSize: 13 }}>
          {detalle}
        </div>
      )}

      {descripcion && (
        <div style={{ marginTop: 14, color: '#8c8c8c', fontSize: 13 }}>
          {descripcion}
        </div>
      )}
    </Modal>
  )
}

export default ModalProgreso
